import { ShiftReportStackerBundle } from "@/pages/shift-report/models/ShiftReportResponse";
import { getColors } from "@/shared/utils/ganttColors";
import { Box, Tooltip } from "@mui/material";
import { ColDef } from "ag-grid-enterprise";

const formatLength = (totalInches: number | null): string => {
	if (totalInches == null) return "";
	const abs = Math.abs(Math.round(totalInches));
	return `${Math.floor(abs / 12)}' ${abs % 12}"`;
};

export const useTableData = () => {
	const columnDefs: ColDef<ShiftReportStackerBundle>[] = [
		{
			field: "HeatName",
			headerName: "Heat",
			minWidth: 80,
			flex: 1,
			valueFormatter: (params) => params.value || "-",
			// * when the group has bundles still to review on Stacker Production
			cellRenderer: (params: {
				value: string | null;
				data?: ShiftReportStackerBundle;
			}) => {
				const pending =
					(params.data?.HeatSuspiciousBundles ?? 0) +
					(params.data?.PendingBundles ?? 0);
				const heatId = params.data?.HeatId;
				return (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						{/* Same heat color as Heats Summary and the Gantt */}
						<Box
							sx={{
								width: 10,
								height: 10,
								borderRadius: "50%",
								flexShrink: 0,
								backgroundColor:
									heatId != null ? getColors(heatId).border : "transparent",
							}}
						/>
						<span>
							{params.value || "-"}
							{pending > 0 && (
								<Tooltip title="Bundles awaiting review on Stacker Production">
									<Box
										component="span"
										sx={{ color: "warning.dark", fontWeight: 700, ml: 0.25 }}
										data-testid="shift-report-stacker-pending-mark"
									>
										*
									</Box>
								</Tooltip>
							)}
						</span>
					</Box>
				);
			},
			// Same position as the heat in the Heats table
			cellClass: "heat-cell",
			cellStyle: { justifyContent: "flex-start" },
		},
		{
			field: "SectionName",
			headerName: "Section",
			minWidth: 80,
			flex: 1,
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "GradeName",
			headerName: "Grade",
			minWidth: 80,
			flex: 1,
			valueFormatter: (params) => params.value || "-",
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "LengthInches",
			headerName: "Length",
			minWidth: 70,
			flex: 1,
			valueFormatter: (params) => formatLength(params.value),
			cellStyle: { justifyContent: "center" },
		},
		{
			headerName: "Good (Bdl × Pcs)",
			minWidth: 140,
			flex: 1.5,
			valueGetter: (params) => params.data?.BundlesBreakdown || "-",
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "TotalPieces",
			headerName: "Total Pieces",
			minWidth: 80,
			flex: 1,
			valueFormatter: (params) =>
				params.value != null ? params.value.toLocaleString() : "",
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "TotalWeight",
			headerName: "Total Weight",
			minWidth: 80,
			flex: 1,
			valueFormatter: (params) =>
				params.value != null ? params.value.toLocaleString() : "-",
			cellStyle: { justifyContent: "center" },
		},
		{
			headerName: "Scrap (Pcs)",
			minWidth: 90,
			flex: 1,
			valueGetter: (params) =>
				params.data?.ScrapPieces
					? `${params.data.ScrapPieces.toLocaleString()} · ${(params.data.ScrapWeight ?? 0).toLocaleString()} t`
					: "-",
			cellStyle: { justifyContent: "center" },
		},
		{
			field: "ScrapReason",
			headerName: "Reason for Scrap",
			minWidth: 120,
			flex: 1.5,
			valueFormatter: (params) => params.value || "-",
			cellStyle: { justifyContent: "center" },
		},
	];

	return { columnDefs };
};
